import { useCallback, useEffect, useRef, useState } from "react";
import { HiPaperAirplane, HiStop, HiTrash } from "react-icons/hi";
import { useChat } from "./ChatProvider";
import { uploadChatAttachment } from "../../controllers/chatController";
import { formatDuration } from "./chatUtils";

const MAX_RECORDING_MS = 5 * 60 * 1000;
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

export const voiceRecordingSupported = () =>
  typeof window !== "undefined" && Boolean(window.MediaRecorder && navigator.mediaDevices?.getUserMedia);

const pickMimeType = () => MIME_CANDIDATES.find((type) => window.MediaRecorder.isTypeSupported?.(type)) || "";

// Records a voice note in place of the message box: record -> preview -> send (or discard).
function VoiceRecorder({ conversationId, replyTo, onDone }) {
  const { sendMessage } = useChat();
  const [stage, setStage] = useState("starting"); // starting | recording | preview | sending | error
  const [elapsed, setElapsed] = useState(0);
  const [clip, setClip] = useState(null); // { blob, url, type, durationMs }
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const discardRef = useRef(false);

  const releaseMic = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const stop = useCallback(() => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    releaseMic();
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Reset per run: React (StrictMode) may mount, clean up and mount this effect again.
    discardRef.current = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const mimeType = pickMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        const chunks = [];
        const startedAt = Date.now();
        recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        recorder.onstop = () => {
          if (discardRef.current) return;
          const type = (recorder.mimeType || mimeType || "audio/webm").split(";")[0];
          const blob = new Blob(chunks, { type });
          setClip({ blob, type, url: URL.createObjectURL(blob), durationMs: Date.now() - startedAt });
          setStage("preview");
        };
        recorderRef.current = recorder;
        recorder.start(250);
        setStage("recording");
        timerRef.current = setInterval(() => {
          const ms = Date.now() - startedAt;
          setElapsed(ms);
          if (ms >= MAX_RECORDING_MS) stop();
        }, 200);
      } catch (err) {
        setError(err.name === "NotAllowedError" ? "Microphone access is blocked. Allow it in your browser to record." : "Couldn't start the microphone.");
        setStage("error");
      }
    })();
    return () => {
      cancelled = true;
      discardRef.current = true;
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      releaseMic();
    };
  }, [stop]);

  useEffect(() => () => clip && URL.revokeObjectURL(clip.url), [clip]);

  const discard = () => {
    discardRef.current = true;
    stop();
    onDone();
  };

  const send = async () => {
    if (!clip) return;
    setStage("sending");
    const extension = clip.type.includes("mp4") ? "m4a" : clip.type.includes("ogg") ? "ogg" : "webm";
    const file = new File([clip.blob], `voice-note-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${extension}`, { type: clip.type });
    try {
      const attachment = await uploadChatAttachment(file, setProgress, { durationMs: clip.durationMs });
      sendMessage(conversationId, { body: "", replyTo, attachments: [attachment] });
      onDone({ sent: true });
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't send the voice message.");
      setStage("preview");
    }
  };

  const iconButton = "p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800";
  return (
    <div className="flex items-center gap-2" data-chat-voice-recorder={stage}>
      <button type="button" onClick={discard} className={iconButton} title="Discard" aria-label="Discard voice message">
        <HiTrash className="w-5 h-5" />
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-2xl bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-sm">
        {stage === "starting" && <span className="text-gray-500">Starting microphone…</span>}
        {stage === "error" && <span className="text-red-600 text-xs">{error}</span>}
        {stage === "recording" && (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
            <span className="font-medium text-gray-800 dark:text-gray-100">Recording</span>
            <span className="ml-auto tabular-nums text-gray-500" aria-live="polite">{formatDuration(elapsed)}</span>
          </>
        )}
        {(stage === "preview" || stage === "sending") && clip && (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio src={clip.url} controls className="h-8 min-w-0 flex-1" />
            <span className="tabular-nums text-xs text-gray-500">{formatDuration(clip.durationMs)}</span>
          </>
        )}
      </div>
      {stage === "recording" && (
        <button type="button" onClick={stop} className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600" title="Stop recording" aria-label="Stop recording">
          <HiStop className="w-5 h-5" />
        </button>
      )}
      {(stage === "preview" || stage === "sending") && (
        <button
          type="button"
          onClick={send}
          disabled={stage === "sending"}
          className="p-2 rounded-full bg-[#2470B7] text-white hover:bg-[#1d5a93] disabled:opacity-60"
          title="Send voice message"
          aria-label="Send voice message"
        >
          {stage === "sending" ? <span className="block w-5 text-[10px] font-semibold">{progress}%</span> : <HiPaperAirplane className="w-5 h-5 rotate-90" />}
        </button>
      )}
      {stage === "preview" && error && <span className="sr-only">{error}</span>}
    </div>
  );
}

export default VoiceRecorder;
